export type HeaderChangeCallback = (headerName: string, value: string | null, previousValue: string | null) => void;

interface HeaderSubscription {
  id: string;
  callback: HeaderChangeCallback;
  headerName?: string;
}

export interface HeaderSubscriptionOptions {
  immediate?: boolean;
}

export interface HTTPPlugin {
  id: string;
  onHeaderChange?: (headerName: string, newValue: string | null, previousValue: string | null) => void;
  onInit?: () => void;
  onDestroy?: () => void;
}

export class HeaderSubscriptionPlugin implements HTTPPlugin {
  id = 'header-subscription';
  private headerSubscriptions: HeaderSubscription[] = [];

  /**
   * Subscribe to header changes
   * @param id Unique identifier for the subscription
   * @param callback Function to call when headers change
   * @param headerName Optional specific header to watch (e.g., 'Authorization')
   * @param options Optional configuration for the subscription
   * @returns Unsubscribe function
   *
   * @example
   * // Listen to all header changes
   * const unsubscribe = plugin.subscribeToHeaderChanges('my-listener', (name, value, prev) => {
   *   console.log(`Header ${name} changed from ${prev} to ${value}`);
   * });
   *
   * @example
   * // Listen only to Authorization header changes
   * const unsubscribe = plugin.subscribeToHeaderChanges(
   *   'auth-listener',
   *   (name, value, prev) => {
   *     console.log(`Auth token changed: ${value}`);
   *   },
   *   'Authorization'
   * );
   *
   * @example
   * // Listen to Authorization header changes and get current value immediately
   * const unsubscribe = plugin.subscribeToHeaderChanges(
   *   'auth-listener',
   *   (name, value, prev) => {
   *     console.log(`Auth token: ${value} (previous: ${prev})`);
   *   },
   *   'Authorization',
   *   { immediate: true }
   * );
   *
   * // Clean up when done
   * unsubscribe();
   */
  subscribeToHeaderChanges = (
    id: string,
    callback: HeaderChangeCallback,
    headerName?: string,
    options?: HeaderSubscriptionOptions,
    getHeaders?: () => Headers
  ): (() => void) => {
    const subscription: HeaderSubscription = {
      id,
      callback,
      headerName,
    };

    this.headerSubscriptions = this.headerSubscriptions.filter((sub) => sub.id !== id);
    this.headerSubscriptions.push(subscription);

    if (options?.immediate && getHeaders) {
      const headers = getHeaders();
      if (headerName) {
        const currentValue = headers.get(headerName);
        try {
          callback(headerName.toLowerCase(), currentValue, null);
        } catch (error) {
          console.error(`Error in immediate header callback for subscription ${id}:`, error);
        }
      } else {
        headers.forEach((value, name) => {
          try {
            callback(name, value, null);
          } catch (error) {
            console.error(`Error in immediate header callback for subscription ${id}:`, error);
          }
        });
      }
    }

    return () => {
      this.headerSubscriptions = this.headerSubscriptions.filter((sub) => sub.id !== id);
    };
  };

  unsubscribeFromHeaderChanges = (id: string) => {
    this.headerSubscriptions = this.headerSubscriptions.filter((sub) => sub.id !== id);
  };

  onHeaderChange = (headerName: string, newValue: string | null, previousValue: string | null) => {
    if (newValue === previousValue) {
      return;
    }

    const normalizedHeaderName = headerName.toLowerCase();

    this.headerSubscriptions.forEach((subscription) => {
      if (subscription.headerName && subscription.headerName.toLowerCase() !== normalizedHeaderName) {
        return;
      }

      try {
        subscription.callback(normalizedHeaderName, newValue, previousValue);
      } catch (error) {
        console.error(`Error in header change callback for subscription ${subscription.id}:`, error);
      }
    });
  };

  onDestroy = () => {
    this.headerSubscriptions = [];
  };
}
